import React, { useEffect, useState, useRef } from 'react'
import { toast } from 'react-hot-toast'
import { FiAlertTriangle, FiCheckCircle, FiXCircle } from 'react-icons/fi'
import { goalsService } from '../services/api'

/**
 * GoalsAlertMonitor - Monitora metas em tempo real e exibe alertas
 *
 * Funcionalidades:
 * - Verifica status das metas a cada 10 minutos
 * - Exibe notificações quando o usuário está abaixo de 80% da meta
 * - Exibe notificações quando ultrapassar limite de inatividade
 * - Badge visual no canto superior direito com status da meta
 */
const GoalsAlertMonitor = () => {
  const [metaStatus, setMetaStatus] = useState(null)
  const [lastAlertTime, setLastAlertTime] = useState(null)
  const intervalRef = useRef(null)

  useEffect(() => {
    // Verificar imediatamente ao montar
    checkGoalStatus()

    // Verificar a cada 10 minutos
    intervalRef.current = setInterval(checkGoalStatus, 10 * 60 * 1000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  const checkGoalStatus = async () => {
    try {
      const response = await goalsService.getDashboardStatus()
      const status = response.data

      setMetaStatus(status)

      // Não mostrar alertas se não há meta configurada
      if (!status || !status.tem_meta) {
        return
      }

      // Evitar spam de notificações - apenas uma vez por hora
      const now = Date.now()
      if (lastAlertTime && (now - lastAlertTime) < 60 * 60 * 1000) {
        return
      }

      // Verificar condições de alerta
      checkAlerts(status)
    } catch (error) {
      console.error('Erro ao verificar status da meta:', error)
    }
  }

  const checkAlerts = (status) => {
    const { progresso, atingiu_meta } = status

    // Array para acumular alertas
    const alertas = []

    // 1. Verificar Produtividade %
    if (progresso.percentual_produtividade?.meta) {
      const percentualAtingimento =
        (progresso.percentual_produtividade.atual / progresso.percentual_produtividade.meta) * 100

      if (!progresso.percentual_produtividade.atingiu) {
        if (percentualAtingimento < 60) {
          alertas.push({
            tipo: 'critico',
            mensagem: `Produtividade crítica: ${progresso.percentual_produtividade.atual.toFixed(1)}% (Meta: ${progresso.percentual_produtividade.meta}%)`,
            icon: FiXCircle,
            color: 'bg-red-500'
          })
        } else if (percentualAtingimento < 80) {
          alertas.push({
            tipo: 'alerta',
            mensagem: `Atenção à produtividade: ${progresso.percentual_produtividade.atual.toFixed(1)}% (Meta: ${progresso.percentual_produtividade.meta}%)`,
            icon: FiAlertTriangle,
            color: 'bg-orange-500'
          })
        }
      }
    }

    // 2. Verificar Horas Produtivas
    if (progresso.horas_produtivas?.meta && !progresso.horas_produtivas.atingiu) {
      const percentualAtingimento = progresso.horas_produtivas.percentual

      if (percentualAtingimento < 60) {
        const horasAtual = Math.floor(progresso.horas_produtivas.atual / 3600)
        const horasMeta = Math.floor(progresso.horas_produtivas.meta / 3600)
        alertas.push({
          tipo: 'critico',
          mensagem: `Horas produtivas muito baixas: ${horasAtual}h (Meta: ${horasMeta}h)`,
          icon: FiXCircle,
          color: 'bg-red-500'
        })
      } else if (percentualAtingimento < 80) {
        const horasAtual = Math.floor(progresso.horas_produtivas.atual / 3600)
        const horasMeta = Math.floor(progresso.horas_produtivas.meta / 3600)
        alertas.push({
          tipo: 'alerta',
          mensagem: `Atenção às horas produtivas: ${horasAtual}h (Meta: ${horasMeta}h)`,
          icon: FiAlertTriangle,
          color: 'bg-orange-500'
        })
      }
    }

    // 3. Verificar Limite de Inatividade
    if (progresso.tempo_inativo?.limite && !progresso.tempo_inativo.dentro_limite) {
      const horasInativo = Math.floor(progresso.tempo_inativo.atual / 3600)
      const horasLimite = Math.floor(progresso.tempo_inativo.limite / 3600)
      const percentualExcedido = ((progresso.tempo_inativo.atual / progresso.tempo_inativo.limite) * 100) - 100

      if (percentualExcedido > 20) {
        alertas.push({
          tipo: 'critico',
          mensagem: `Tempo inativo excedeu em ${percentualExcedido.toFixed(0)}%! (${horasInativo}h / Limite: ${horasLimite}h)`,
          icon: FiXCircle,
          color: 'bg-red-500'
        })
      } else {
        alertas.push({
          tipo: 'alerta',
          mensagem: `Tempo inativo acima do limite: ${horasInativo}h (Limite: ${horasLimite}h)`,
          icon: FiAlertTriangle,
          color: 'bg-orange-500'
        })
      }
    }

    // Exibir alertas
    if (alertas.length > 0) {
      mostrarAlertas(alertas, atingiu_meta)
      setLastAlertTime(Date.now())
    }
  }

  const mostrarAlertas = (alertas, atingiuMeta) => {
    // Se há alertas críticos, mostrar primeiro
    const criticos = alertas.filter(a => a.tipo === 'critico')
    const avisos = alertas.filter(a => a.tipo === 'alerta')

    // Mostrar alertas críticos
    criticos.forEach((alerta) => {
      toast.error(
        <div className="flex items-start gap-2">
          <alerta.icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Meta não atingida</p>
            <p className="text-sm">{alerta.mensagem}</p>
          </div>
        </div>,
        {
          duration: 8000,
          position: 'top-right',
          style: {
            maxWidth: '400px'
          }
        }
      )
    })

    // Mostrar avisos
    avisos.forEach((alerta) => {
      toast(
        <div className="flex items-start gap-2">
          <alerta.icon className="w-5 h-5 flex-shrink-0 mt-0.5 text-orange-600" />
          <div>
            <p className="font-semibold text-orange-900">Atenção à sua meta</p>
            <p className="text-sm text-orange-800">{alerta.mensagem}</p>
          </div>
        </div>,
        {
          duration: 6000,
          position: 'top-right',
          icon: '⚠️',
          style: {
            background: '#fff7ed',
            border: '1px solid #fb923c',
            maxWidth: '400px'
          }
        }
      )
    })

    // Se não atingiu nenhuma meta geral, mostrar notificação resumida
    if (!atingiuMeta && alertas.length === 0) {
      toast(
        <div className="flex items-start gap-2">
          <FiAlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-600" />
          <div>
            <p className="font-semibold text-yellow-900">Meta em progresso</p>
            <p className="text-sm text-yellow-800">Continue se esforçando para atingir suas metas!</p>
          </div>
        </div>,
        {
          duration: 4000,
          position: 'top-right',
          icon: '💪',
          style: {
            background: '#fefce8',
            border: '1px solid #eab308',
            maxWidth: '400px'
          }
        }
      )
    }
  }

  // Badge visual (opcional - pode ser adicionado ao header/sidebar)
  if (!metaStatus || !metaStatus.tem_meta) {
    return null
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {metaStatus.atingiu_meta ? (
        <div className="bg-green-100 border-2 border-green-500 text-green-900 rounded-full px-4 py-2 shadow-lg flex items-center gap-2 animate-pulse">
          <FiCheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-sm font-semibold">Meta atingida!</span>
        </div>
      ) : (
        <div className="bg-orange-100 border-2 border-orange-500 text-orange-900 rounded-full px-4 py-2 shadow-lg flex items-center gap-2">
          <FiAlertTriangle className="w-5 h-5 text-orange-600" />
          <span className="text-sm font-semibold">Em progresso</span>
        </div>
      )}
    </div>
  )
}

export default GoalsAlertMonitor
